import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../apiClient';
import { getCrmLead } from '../../api/leadsApi';
import CustomerPopup from '../../components/Profile/CustomerPopup';
import './quote-create.css';

/* NEW QUOTE — pick who it's for, then compose in their card.

   WHAT THIS REPLACED. /quotes/create used to be a side-by-side page with its own
   customer fields, its own service picker and its own pricing. Opening it from a
   customer's record threw that customer away and made you type them again; its
   pricing was a second source of truth next to the quote API's; and it was the
   only surface where a quote could be built, so the customer card could show
   quotes but never make one.

   Composing now happens in ONE place — the customer card's Quotes tab — and this
   page's only job is to answer "which customer?" before handing off. That is why
   the route survives while the page's old body is gone: five callers reach a
   quote from somewhere that has no customer yet.

     LeadWorkQueue   "Create quote" on a qualified lead   -> {leadId}
     EventsCalendar  "+ New Booking"                      -> {eventDate}
     UnifiedInbox    "New quote"                          -> nothing
     Revival/Overview "New quote"                         -> nothing
     CustomerPopup   (already has a customer)             -> {customer}

   Deleting the route would have left five dead buttons. Deleting the PAGE —
   the side-by-side form — is what was actually wanted.

   THE LEAD PREFILL WAS ALREADY BROKEN. LeadWorkQueue sends {leadId}; the old
   page read location.state.customer || location.state.lead and never looked at
   leadId, so "Create quote" on a qualified lead opened an empty form. This
   resolves leadId into a real record, so that button now carries the lead.

   eventDate is accepted and passed through, but it is NOT written into the quote
   here. The event date belongs to the offering's intake_schema, which the
   composer renders and the server persists to Quote.intake_data. Writing it in
   two places is how the two disagree. */

export default function QuoteCreate() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location?.state || {};

  const [chosen, setChosen] = useState(state.customer || null);
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState([]);
  const [searching, setSearching] = useState(false);
  const [resolving, setResolving] = useState(!!state.leadId);
  const [error, setError] = useState('');

  /* A lead id arrives from the work queue. Resolve it to a record so the card
     opens on the actual person rather than on an empty form. */
  useEffect(() => {
    if (!state.leadId || chosen) return undefined;
    let live = true;
    (async () => {
      try {
        // The real path is /leads/crm-leads/<id>/ — use the existing helper
        // rather than a hand-written URL. My first guess omitted the /leads
        // prefix and would have 404'd into the "couldn't open that lead"
        // branch, which looks like a missing lead rather than a bad URL.
        const res = await getCrmLead(state.leadId);
        if (live && res?.data) setChosen({ ...res.data, object: 'lead' });
      } catch {
        // Say so rather than silently showing a search box as if no lead was
        // ever named — the user clicked a button on a specific lead.
        if (live) setError("Couldn't open that lead. Search for them below.");
      } finally {
        if (live) setResolving(false);
      }
    })();
    return () => { live = false; };
  }, [state.leadId, chosen]);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setRows([]); return undefined; }
    let live = true;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        // The global search endpoint — same ranking the top bar uses, so the
        // customer you find here is the one you'd find there.
        const res = await api.get('/search/', { params: { q } });
        if (!live) return;
        const d = res?.data || {};
        setRows([...(d.results || []), ...(d.leads || [])].slice(0, 8));
        setError('');
      } catch {
        if (live) { setRows([]); setError("Search isn't responding right now."); }
      } finally {
        if (live) setSearching(false);
      }
    }, 150);
    return () => { live = false; clearTimeout(t); };
  }, [query]);

  const openFor = useCallback((row) => {
    // The card fetches its own full record from the id, so a search row is
    // enough to open it.
    setChosen({
      id: row.id,
      customer_id: row.route?.customer_id || row.id,
      object: row.result_type === 'lead' ? 'lead' : 'customer',
      full_name: row.label,
      primary_phone: row.phone,
    });
  }, []);

  const close = () => {
    setChosen(null);
    // Back to wherever they came from, not to a dead page.
    if (window.history.length > 1) navigate(-1);
    else navigate('/revival');
  };

  const heading = useMemo(() => {
    if (state.eventDate) return `New booking for ${state.eventDate}`;
    return 'New quote';
  }, [state.eventDate]);

  if (chosen) {
    return (
      <CustomerPopup
        lead={chosen}
        leadType={chosen.object === 'lead' ? 'lead' : 'customer'}
        initialTab="revival"
        openComposer
        onClose={close}
      />
    );
  }

  return (
    <div className="qcr-page">
      <div className="qcr-card">
        <h1 className="qcr-title">{heading}</h1>
        <p className="qcr-sub">Who is this quote for?</p>

        {resolving ? <p className="qcr-muted">Opening that lead…</p> : null}
        {error ? <p className="qcr-error">{error}</p> : null}

        <input
          className="qcr-input"
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, address, phone or account number"
          aria-label="Search for a customer or lead"
        />

        {searching && !rows.length ? <p className="qcr-muted">Searching…</p> : null}
        {!searching && query.trim().length >= 2 && !rows.length ? (
          <p className="qcr-muted">No match for “{query.trim()}”.</p>
        ) : null}

        <ul className="qcr-results">
          {rows.map((r) => (
            <li key={`${r.result_type}-${r.id}`}>
              <button type="button" className="qcr-row" onClick={() => openFor(r)}>
                <span className="qcr-row-name">
                  {r.label}
                  {r.result_type === 'lead' ? <span className="qcr-kind">lead</span> : null}
                </span>
                <span className="qcr-row-meta">
                  {[r.id_line, r.address, r.phone].filter(Boolean).join(' · ')}
                </span>
              </button>
            </li>
          ))}
        </ul>

        <div className="qcr-actions">
          {/* A quote for someone not on file yet: open a blank card and let the
              normal customer form do the work, rather than a second set of
              name/address fields living on this page. */}
          <button
            type="button"
            className="qcr-btn qcr-btn-primary"
            onClick={() => setChosen({ object: 'customer', __new: true })}
          >
            New customer
          </button>
          <button type="button" className="qcr-btn" onClick={close}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
